// src/components/Procurement/ProcurementAIInsightsPanel.jsx
import React, { useEffect, useMemo, useState } from "react";
import { Link } from "react-router-dom";
import api from "@/lib/axios";
import { toast } from "react-hot-toast";
import { useUserPermissions } from "@/hooks/useUserPermissions";

const SEVERITY_STYLES = {
  critical: {
    card: "border-red-500/60 bg-red-950/30",
    pill: "bg-red-500/15 text-red-200 border-red-400/40",
    dot: "bg-red-400",
    label: "Critical",
  },
  high: {
    card: "border-orange-500/60 bg-orange-950/30",
    pill: "bg-orange-500/15 text-orange-200 border-orange-400/40",
    dot: "bg-orange-400",
    label: "High",
  },
  medium: {
    card: "border-yellow-500/50 bg-yellow-950/20",
    pill: "bg-yellow-500/15 text-yellow-200 border-yellow-400/40",
    dot: "bg-yellow-300",
    label: "Medium",
  },
  low: {
    card: "border-cyan-500/40 bg-cyan-950/20",
    pill: "bg-cyan-500/15 text-cyan-200 border-cyan-400/40",
    dot: "bg-cyan-300",
    label: "Low",
  },
  info: {
    card: "border-slate-600 bg-slate-900/80",
    pill: "bg-slate-700/60 text-slate-200 border-slate-500/40",
    dot: "bg-slate-300",
    label: "Info",
  },
};

const NARRATIVE_STYLES = {
  urgent: "border-red-500/50 bg-red-950/20 text-red-100",
  warning: "border-orange-500/50 bg-orange-950/20 text-orange-100",
  positive: "border-emerald-500/40 bg-emerald-950/20 text-emerald-100",
  neutral: "border-cyan-500/40 bg-cyan-950/20 text-cyan-100",
  info: "border-slate-600/60 bg-slate-900/70 text-slate-100",
};

const ENTITY_LABELS = {
  purchase_order: "Purchase Order",
  vendor: "Vendor",
  event: "Event",
  invoice: "Invoice",
  procurement: "Procurement",
};

const ACTION_STYLE = {
  primary:
    "border-cyan-500/50 bg-cyan-600/20 hover:bg-cyan-500/30 text-cyan-100",
  neutral:
    "border-slate-600 bg-slate-900/80 hover:bg-slate-800 text-slate-200",
  warning:
    "border-orange-500/50 bg-orange-950/30 hover:bg-orange-900/40 text-orange-100",
  success:
    "border-emerald-500/50 bg-emerald-950/30 hover:bg-emerald-900/40 text-emerald-100",
  danger:
    "border-red-500/50 bg-red-950/30 hover:bg-red-900/40 text-red-100",
};

function getSeverityStyle(severity) {
  return SEVERITY_STYLES[String(severity || "").toLowerCase()] || SEVERITY_STYLES.info;
}

function formatDate(value) {
  if (!value) return "—";

  try {
    return new Date(value).toLocaleString();
  } catch {
    return String(value);
  }
}

function formatRuleName(value) {
  const text = String(value || "").trim();
  if (!text) return "Unknown rule";

  return text
    .replace(/^procurement_/, "")
    .replace(/_/g, " ")
    .replace(/\b\w/g, (char) => char.toUpperCase());
}

function pct(value) {
  const n = Number(value);
  if (!Number.isFinite(n)) return 0;
  return Math.max(0, Math.min(100, Math.round(n)));
}

function getInsightKey(insight) {
  return insight?.insight_id || `${insight?.entity_type}-${insight?.entity_id}-${insight?.title}`;
}

function normalizeInsights(payload) {
  if (Array.isArray(payload)) return payload;
  if (Array.isArray(payload?.insights)) return payload.insights;
  return [];
}

function getReason(insight) {
  return insight?.reason_json && typeof insight.reason_json === "object"
    ? insight.reason_json
    : {};
}

function getInsightRule(insight) {
  return (
    getReason(insight)?.rule ||
    `${insight?.entity_type || "general"}_${insight?.severity || "info"}`
  );
}

function getRelatedRoute(insight) {
  const entityType = insight?.entity_type;
  const entityId = insight?.entity_id;
  const reason = getReason(insight);

  if (!entityType || !entityId) return null;

  if (entityType === "purchase_order") {
    return `/procurement/po-details/${entityId}`;
  }

  if (entityType === "vendor") {
    return "/vendors";
  }

  if (entityType === "event") {
    return `/events/${entityId}`;
  }

  if (entityType === "invoice") {
    const invoiceId = reason.invoice_id || entityId;
    return `/invoice/view/${invoiceId}`;
  }

  return null;
}

function getRelatedLabel(insight) {
  const entityType = insight?.entity_type;
  const entityId = insight?.entity_id;
  const reason = getReason(insight);

  if (entityType === "purchase_order") {
    const poNumber = reason.po_number || reason.po_id || entityId;
    return `Open PO ${poNumber} →`;
  }

  if (entityType === "vendor") {
    const vendorName = reason.vendor_name || `Vendor #${entityId}`;
    return `Open ${vendorName} →`;
  }

  if (entityType === "event") {
    const eventName = reason.event_name || `Event #${entityId}`;
    return `Open ${eventName} →`;
  }

  if (entityType === "invoice") {
    return `Open Invoice #${entityId} →`;
  }

  return "Open related record →";
}

function getEntityDisplay(insight) {
  const reason = getReason(insight);
  const entityType = insight?.entity_type;
  const entityId = insight?.entity_id;

  if (entityType === "purchase_order") {
    return reason.po_number || `PO #${entityId}`;
  }

  if (entityType === "vendor") {
    return reason.vendor_name || `Vendor #${entityId}`;
  }

  if (entityType === "event") {
    return reason.event_name || `Event #${entityId}`;
  }

  if (entityType === "invoice") {
    return `Invoice #${entityId}`;
  }

  return entityId ? `#${entityId}` : "";
}

function normalizeLearningSummary(payload) {
  return payload && typeof payload === "object" ? payload : null;
}

function getSmartActions(insight) {
  const rule = getInsightRule(insight);
  const entityType = insight?.entity_type;

  if (entityType === "purchase_order" && rule === "late_delivery_no_receiving") {
    return [
      {
        key: "supplier_contacted",
        label: "Supplier contacted",
        description: "Acknowledge this risk after contacting the supplier.",
        status: "acknowledged",
        tone: "primary",
      },
      {
        key: "receiving_checking",
        label: "Receiving checking",
        description: "Acknowledge while the receiving team checks delivery.",
        status: "acknowledged",
        tone: "neutral",
      },
      {
        key: "manager_review",
        label: "Needs manager review",
        description: "Keep this visible but mark as acknowledged.",
        status: "acknowledged",
        tone: "warning",
      },
    ];
  }

  if (entityType === "purchase_order" && rule === "draft_po_missing_required_data") {
    return [
      {
        key: "draft_in_progress",
        label: "Draft in progress",
        description: "Acknowledge this draft as being worked on.",
        status: "acknowledged",
        tone: "primary",
      },
      {
        key: "planner_review",
        label: "Needs planner review",
        description: "Acknowledge and leave the draft visible in acknowledged view.",
        status: "acknowledged",
        tone: "warning",
      },
      {
        key: "not_relevant",
        label: "Not relevant now",
        description: "Dismiss this insight if the draft is intentionally incomplete.",
        feedback: "dismissed",
        tone: "danger",
      },
    ];
  }

  if (entityType === "purchase_order" && rule === "invoice_total_above_po_total") {
    return [
      {
        key: "finance_review",
        label: "Finance reviewing",
        description: "Acknowledge while finance checks invoice variance.",
        status: "acknowledged",
        tone: "warning",
      },
      {
        key: "checked_resolved",
        label: "Checked & resolved",
        description: "Resolve this if the invoice variance has been explained.",
        feedback: "resolved",
        tone: "success",
      },
      {
        key: "not_useful",
        label: "Not useful",
        description: "Record that this invoice warning was not useful.",
        feedback: "not_useful",
        tone: "neutral",
      },
    ];
  }

  if (entityType === "vendor" || rule === "vendor_repeated_rejection_risk") {
    return [
      {
        key: "vendor_review",
        label: "Vendor under review",
        description: "Acknowledge while vendor performance is being reviewed.",
        status: "acknowledged",
        tone: "warning",
      },
      {
        key: "useful_vendor_risk",
        label: "Useful vendor risk",
        description: "Tell the AI this vendor warning was useful.",
        feedback: "useful",
        tone: "primary",
      },
      {
        key: "dismiss_vendor_risk",
        label: "Dismiss vendor risk",
        description: "Dismiss if this vendor pattern is not relevant.",
        feedback: "dismissed",
        tone: "danger",
      },
    ];
  }

  if (entityType === "event" || rule === "event_procurement_gap") {
    return [
      {
        key: "event_procurement_review",
        label: "Review event buying",
        description: "Acknowledge while event procurement is reviewed.",
        status: "acknowledged",
        tone: "primary",
      },
      {
        key: "event_gap_resolved",
        label: "Procurement resolved",
        description: "Resolve if event procurement is already covered.",
        feedback: "resolved",
        tone: "success",
      },
      {
        key: "dismiss_event_gap",
        label: "Dismiss event gap",
        description: "Dismiss if procurement is not required for this event.",
        feedback: "dismissed",
        tone: "danger",
      },
    ];
  }

  return [
    {
      key: "acknowledge",
      label: "Acknowledge",
      description: "Mark this insight as reviewed.",
      status: "acknowledged",
      tone: "primary",
    },
    {
      key: "resolve",
      label: "Resolve",
      description: "Mark this insight as resolved.",
      feedback: "resolved",
      tone: "success",
    },
    {
      key: "dismiss",
      label: "Dismiss",
      description: "Dismiss this insight from the open feed.",
      feedback: "dismissed",
      tone: "danger",
    },
  ];
}

export default function ProcurementAIInsightsPanel({
  autoRefresh = true,
  compact = false,
  maxHeight = "max-h-[620px]",
}) {
  const {
    isAdmin,
    isDeveloper,
    isManager,
    isProcurement,
    isFinance,
  } = useUserPermissions();

  const canView =
    Boolean(isAdmin) ||
    Boolean(isDeveloper) ||
    Boolean(isManager) ||
    Boolean(isProcurement) ||
    Boolean(isFinance);

  const [loading, setLoading] = useState(false);
  const [refreshing, setRefreshing] = useState(false);
  const [learningLoading, setLearningLoading] = useState(false);
  const [insights, setInsights] = useState([]);
  const [learningSummary, setLearningSummary] = useState(null);
  const [statusFilter, setStatusFilter] = useState("open");
  const [severityFilter, setSeverityFilter] = useState("all");
  const [showLearningDetails, setShowLearningDetails] = useState(!compact);
  const [lastGeneratedCount, setLastGeneratedCount] = useState(null);
  const [lastSuppressedCount, setLastSuppressedCount] = useState(null);
  const [lastLoadedAt, setLastLoadedAt] = useState(null);
  const [lastLearningLoadedAt, setLastLearningLoadedAt] = useState(null);
  const [actionBusyId, setActionBusyId] = useState(null);

  const loadLearningSummary = async () => {
    if (!canView) return;

    setLearningLoading(true);

    try {
      const res = await api.get("/ai/procurement/learning-summary", {
        params: {
          _ts: Date.now(),
        },
      });

      setLearningSummary(normalizeLearningSummary(res?.data));
      setLastLearningLoadedAt(new Date().toISOString());
    } catch (err) {
      console.error("Procurement AI learning summary load failed:", err);
      setLearningSummary(null);
    } finally {
      setLearningLoading(false);
    }
  };

  const loadInsights = async ({ refresh = false, includeLearning = true } = {}) => {
    if (!canView) return;

    if (refresh) setRefreshing(true);
    else setLoading(true);

    try {
      const res = await api.get("/ai/procurement/insights", {
        params: {
          refresh,
          status: statusFilter,
          limit: 150,
          _ts: Date.now(),
        },
      });

      const nextInsights = normalizeInsights(res?.data);

      setInsights(nextInsights);
      setLastGeneratedCount(
        typeof res?.data?.generated_count === "number" ? res.data.generated_count : null
      );
      setLastSuppressedCount(
        typeof res?.data?.suppressed_count === "number" ? res.data.suppressed_count : null
      );
      setLastLoadedAt(new Date().toISOString());

      if (includeLearning) {
        loadLearningSummary();
      }

      if (refresh) {
        const generatedText =
          typeof res?.data?.generated_count === "number"
            ? ` (${res.data.generated_count} generated`
            : "";

        const suppressedText =
          typeof res?.data?.suppressed_count === "number" && res.data.suppressed_count > 0
            ? `, ${res.data.suppressed_count} suppressed`
            : "";

        toast.success(
          `AI insights refreshed${generatedText ? `${generatedText}${suppressedText})` : ""}`
        );
      }
    } catch (err) {
      console.error("Procurement AI insights load failed:", err);
      toast.error(
        err?.response?.data?.error ||
          err?.response?.data?.details ||
          "Failed to load procurement AI insights"
      );
      setInsights([]);
    } finally {
      setLoading(false);
      setRefreshing(false);
    }
  };

  useEffect(() => {
    if (!canView) return;
    loadInsights({ refresh: autoRefresh, includeLearning: true });
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [canView, statusFilter]);

  const filteredInsights = useMemo(() => {
    if (severityFilter === "all") return insights;

    return insights.filter(
      (item) => String(item?.severity || "").toLowerCase() === severityFilter
    );
  }, [insights, severityFilter]);

  const insightCounts = useMemo(() => {
    return insights.reduce(
      (acc, item) => {
        const sev = String(item?.severity || "info").toLowerCase();
        acc.total += 1;
        acc[sev] = (acc[sev] || 0) + 1;
        return acc;
      },
      {
        total: 0,
        critical: 0,
        high: 0,
        medium: 0,
        low: 0,
        info: 0,
      }
    );
  }, [insights]);

  const sendFeedback = async (insight, feedback) => {
    const insightId = insight?.insight_id;
    if (!insightId) return;

    setActionBusyId(insightId);

    try {
      await api.post(`/ai/procurement/insights/${insightId}/feedback`, {
        feedback,
      });

      if (feedback === "dismissed" || feedback === "resolved") {
        setInsights((prev) => prev.filter((row) => row.insight_id !== insightId));
      }

      await loadLearningSummary();

      if (feedback === "useful") toast.success("Feedback saved: useful");
      if (feedback === "not_useful") toast.success("Feedback saved");
      if (feedback === "dismissed") toast.success("Insight dismissed");
      if (feedback === "resolved") toast.success("Insight marked as resolved");
    } catch (err) {
      console.error("AI insight feedback failed:", err);
      toast.error(err?.response?.data?.error || "Failed to save feedback");
    } finally {
      setActionBusyId(null);
    }
  };

  const updateStatus = async (insight, nextStatus) => {
    const insightId = insight?.insight_id;
    if (!insightId) return;

    setActionBusyId(insightId);

    try {
      await api.patch(`/ai/procurement/insights/${insightId}/status`, {
        status: nextStatus,
      });

      if (nextStatus === "dismissed" || nextStatus === "resolved") {
        setInsights((prev) => prev.filter((row) => row.insight_id !== insightId));
      } else {
        setInsights((prev) =>
          prev.map((row) =>
            row.insight_id === insightId ? { ...row, status: nextStatus } : row
          )
        );
      }

      await loadLearningSummary();

      toast.success(`Insight ${nextStatus}`);
    } catch (err) {
      console.error("AI insight status update failed:", err);
      toast.error(err?.response?.data?.error || "Failed to update insight");
    } finally {
      setActionBusyId(null);
    }
  };

  const handleSmartAction = async (insight, action) => {
    if (!insight?.insight_id || !action) return;

    if (action.feedback) {
      await sendFeedback(insight, action.feedback);
      return;
    }

    if (action.status) {
      await updateStatus(insight, action.status);
    }
  };

  if (!canView) {
    return null;
  }

  return (
    <section className="rounded-2xl border border-cyan-700/40 bg-slate-950/80 shadow-2xl shadow-cyan-950/20 overflow-hidden">
      <div className="border-b border-slate-800 bg-gradient-to-r from-slate-950 via-slate-900 to-cyan-950/40 p-4">
        <div className="flex flex-col xl:flex-row xl:items-center xl:justify-between gap-4">
          <div>
            <div className="flex items-center gap-2">
              <span className="h-2.5 w-2.5 rounded-full bg-cyan-300 shadow shadow-cyan-300/70" />
              <h2 className="text-xl font-bold text-cyan-200">
                AI Procurement Intelligence
              </h2>
            </div>

            <p className="text-sm text-slate-400 mt-1">
              Live operational insights generated from draft POs, vendors, deliveries,
              invoices, and event procurement data.
            </p>

            {lastLoadedAt && (
              <p className="text-xs text-slate-500 mt-1">
                Last checked: {formatDate(lastLoadedAt)}
                {lastGeneratedCount !== null ? ` · Generated: ${lastGeneratedCount}` : ""}
                {lastSuppressedCount ? ` · Suppressed: ${lastSuppressedCount}` : ""}
                {lastLearningLoadedAt ? ` · Learning synced: ${formatDate(lastLearningLoadedAt)}` : ""}
              </p>
            )}
          </div>

          <div className="flex flex-col sm:flex-row gap-2">
            <select
              value={statusFilter}
              onChange={(e) => setStatusFilter(e.target.value)}
              className="rounded-lg bg-slate-900 border border-slate-700 px-3 py-2 text-sm text-white focus:outline-none focus:ring-2 focus:ring-cyan-500"
            >
              <option value="open">Open</option>
              <option value="acknowledged">Acknowledged</option>
              <option value="resolved">Resolved</option>
              <option value="dismissed">Dismissed</option>
              <option value="all">All</option>
            </select>

            <select
              value={severityFilter}
              onChange={(e) => setSeverityFilter(e.target.value)}
              className="rounded-lg bg-slate-900 border border-slate-700 px-3 py-2 text-sm text-white focus:outline-none focus:ring-2 focus:ring-cyan-500"
            >
              <option value="all">All severity</option>
              <option value="critical">Critical</option>
              <option value="high">High</option>
              <option value="medium">Medium</option>
              <option value="low">Low</option>
              <option value="info">Info</option>
            </select>

            <button
              type="button"
              onClick={() => loadInsights({ refresh: true, includeLearning: true })}
              disabled={refreshing || loading}
              className="rounded-lg bg-cyan-600 hover:bg-cyan-500 disabled:opacity-50 disabled:cursor-not-allowed px-4 py-2 text-sm font-semibold text-white shadow"
            >
              {refreshing ? "Refreshing…" : "Refresh AI"}
            </button>
          </div>
        </div>

        {!compact && (
          <div className="grid grid-cols-2 md:grid-cols-5 gap-2 mt-4">
            <MiniStat label="Total" value={insightCounts.total} />
            <MiniStat label="High" value={insightCounts.high + insightCounts.critical} tone="high" />
            <MiniStat label="Medium" value={insightCounts.medium} tone="medium" />
            <MiniStat label="Low" value={insightCounts.low} tone="low" />
            <MiniStat label="Info" value={insightCounts.info} tone="info" />
          </div>
        )}
      </div>

      <div className={`p-4 overflow-y-auto ${maxHeight}`}>
        <LearningCockpit
          compact={compact}
          loading={learningLoading}
          summary={learningSummary}
          showDetails={showLearningDetails}
          onToggleDetails={() => setShowLearningDetails((prev) => !prev)}
          onRefresh={loadLearningSummary}
        />

        {loading ? (
          <div className="rounded-xl border border-slate-800 bg-slate-900/70 p-5 text-slate-300 mt-4">
            Loading AI procurement insights…
          </div>
        ) : filteredInsights.length === 0 ? (
          <div className="rounded-xl border border-emerald-700/40 bg-emerald-950/20 p-5 mt-4">
            <div className="text-emerald-200 font-semibold">No AI risks found</div>
            <p className="text-sm text-emerald-100/70 mt-1">
              Procurement intelligence did not find any matching issues for the current
              filter. Use Refresh AI to scan again.
            </p>
          </div>
        ) : (
          <div className="space-y-3 mt-4">
            {filteredInsights.map((insight) => (
              <InsightCard
                key={getInsightKey(insight)}
                insight={insight}
                busy={actionBusyId === insight.insight_id}
                onUseful={() => sendFeedback(insight, "useful")}
                onNotUseful={() => sendFeedback(insight, "not_useful")}
                onAcknowledge={() => updateStatus(insight, "acknowledged")}
                onResolve={() => sendFeedback(insight, "resolved")}
                onDismiss={() => sendFeedback(insight, "dismissed")}
                onSmartAction={(action) => handleSmartAction(insight, action)}
              />
            ))}
          </div>
        )}
      </div>
    </section>
  );
}

function LearningCockpit({
  compact,
  loading,
  summary,
  showDetails,
  onToggleDetails,
  onRefresh,
}) {
  const overview = summary?.overview || {};
  const narrative = Array.isArray(summary?.narrative) ? summary.narrative : [];
  const topUsefulRules = Array.isArray(summary?.top_useful_rules)
    ? summary.top_useful_rules
    : [];
  const mostDismissedRules = Array.isArray(summary?.most_dismissed_rules)
    ? summary.most_dismissed_rules
    : [];
  const recentActivity = Array.isArray(summary?.activity?.recent_activity)
    ? summary.activity.recent_activity
    : [];
  const preferences = summary?.preferences || {};

  const usefulnessScore = pct(overview.ai_usefulness_score);
  const totalFeedback = Number(overview.total_feedback || 0);
  const openHighRisk = Number(overview.high_or_critical_open || 0);
  const preferenceCount = Number(overview.preference_count || 0);
  const activity7d = Number(overview.activity_7d || 0);

  const scoreTone =
    usefulnessScore >= 70
      ? "text-emerald-200 border-emerald-500/40 bg-emerald-950/20"
      : usefulnessScore >= 40
        ? "text-yellow-200 border-yellow-500/40 bg-yellow-950/20"
        : "text-orange-200 border-orange-500/40 bg-orange-950/20";

  return (
    <div className="rounded-2xl border border-cyan-700/40 bg-gradient-to-br from-slate-950 via-slate-900 to-cyan-950/20 p-4 shadow-lg shadow-cyan-950/10">
      <div className="flex flex-col xl:flex-row xl:items-start xl:justify-between gap-4">
        <div>
          <div className="flex items-center gap-2">
            <span className="h-2.5 w-2.5 rounded-full bg-emerald-300 shadow shadow-emerald-300/70 animate-pulse" />
            <h3 className="text-lg font-bold text-emerald-100">
              AI Learning Cockpit
            </h3>
          </div>

          <p className="text-sm text-slate-400 mt-1">
            Tracks how users respond to AI insights and adapts procurement intelligence over time.
          </p>
        </div>

        <div className="flex gap-2">
          <button
            type="button"
            onClick={onRefresh}
            disabled={loading}
            className="rounded-lg border border-slate-700 bg-slate-900 hover:bg-slate-800 disabled:opacity-50 px-3 py-1.5 text-xs font-semibold text-slate-200"
          >
            {loading ? "Syncing…" : "Sync learning"}
          </button>

          <button
            type="button"
            onClick={onToggleDetails}
            className="rounded-lg border border-cyan-700/60 bg-cyan-950/30 hover:bg-cyan-900/40 px-3 py-1.5 text-xs font-semibold text-cyan-100"
          >
            {showDetails ? "Hide details" : "Show details"}
          </button>
        </div>
      </div>

      <div className="grid grid-cols-2 lg:grid-cols-5 gap-2 mt-4">
        <LearningStat
          label="Usefulness"
          value={`${usefulnessScore}%`}
          sub={`${totalFeedback} feedback`}
          className={scoreTone}
        />
        <LearningStat
          label="Resolved"
          value={overview.resolved_count || 0}
          sub={`${pct(overview.resolution_rate)}% rate`}
          className="text-emerald-200 border-emerald-600/40 bg-emerald-950/20"
        />
        <LearningStat
          label="Dismissed"
          value={overview.dismissed_count || 0}
          sub={`${pct(overview.dismissal_rate)}% rate`}
          className="text-orange-200 border-orange-600/40 bg-orange-950/20"
        />
        <LearningStat
          label="High-risk open"
          value={openHighRisk}
          sub={`${overview.open_insights || 0} open total`}
          className="text-red-200 border-red-600/40 bg-red-950/20"
        />
        <LearningStat
          label="Memory"
          value={preferenceCount}
          sub={`${activity7d} actions / 7d`}
          className="text-cyan-200 border-cyan-600/40 bg-cyan-950/20"
        />
      </div>

      {narrative.length > 0 && (
        <div className="grid grid-cols-1 lg:grid-cols-2 gap-2 mt-4">
          {narrative.slice(0, compact ? 2 : 4).map((item, index) => (
            <NarrativeCard key={`${item.title}-${index}`} item={item} />
          ))}
        </div>
      )}

      {showDetails && !compact && (
        <div className="grid grid-cols-1 xl:grid-cols-3 gap-3 mt-4">
          <RuleList
            title="Top useful intelligence"
            empty="No useful rule feedback yet."
            rows={topUsefulRules}
            valueKey="usefulness_percent"
            valueSuffix="%"
            tone="positive"
          />

          <RuleList
            title="Most dismissed"
            empty="No dismissed rule patterns yet."
            rows={mostDismissedRules}
            valueKey="dismissed_count"
            valueSuffix=" dismissals"
            tone="warning"
          />

          <ActivityList
            rows={recentActivity}
            preferenceCount={preferences.preference_count || 0}
          />
        </div>
      )}
    </div>
  );
}

function LearningStat({ label, value, sub, className }) {
  return (
    <div className={`rounded-xl border p-3 ${className}`}>
      <div className="text-xs opacity-75">{label}</div>
      <div className="text-2xl font-black mt-1">{value}</div>
      {sub && <div className="text-xs opacity-70 mt-1">{sub}</div>}
    </div>
  );
}

function NarrativeCard({ item }) {
  const tone = NARRATIVE_STYLES[item?.tone] || NARRATIVE_STYLES.info;

  return (
    <div className={`rounded-xl border p-3 ${tone}`}>
      <div className="text-sm font-bold">{item?.title || "AI learning note"}</div>
      <p className="text-xs opacity-80 mt-1 leading-relaxed">
        {item?.message || "No learning message available."}
      </p>
    </div>
  );
}

function RuleList({
  title,
  empty,
  rows,
  valueKey,
  valueSuffix = "",
  tone = "positive",
}) {
  const toneClass =
    tone === "warning"
      ? "border-orange-700/40 bg-orange-950/10"
      : "border-emerald-700/40 bg-emerald-950/10";

  return (
    <div className={`rounded-xl border ${toneClass} p-3`}>
      <div className="text-sm font-bold text-white">{title}</div>

      {!rows?.length ? (
        <p className="text-xs text-slate-500 mt-2">{empty}</p>
      ) : (
        <div className="space-y-2 mt-3">
          {rows.slice(0, 5).map((row) => {
            const value = row?.[valueKey] ?? 0;

            return (
              <div
                key={row.insight_rule}
                className="rounded-lg border border-slate-800 bg-slate-950/60 p-2"
              >
                <div className="flex items-start justify-between gap-2">
                  <div className="text-xs font-semibold text-slate-200">
                    {formatRuleName(row.insight_rule)}
                  </div>
                  <div className="text-xs text-cyan-200 shrink-0">
                    {value}
                    {valueSuffix}
                  </div>
                </div>

                <div className="text-[11px] text-slate-500 mt-1">
                  Feedback {row.total_feedback || 0} · Useful {row.useful_count || 0} · Resolved{" "}
                  {row.resolved_count || 0}
                </div>
              </div>
            );
          })}
        </div>
      )}
    </div>
  );
}

function ActivityList({ rows, preferenceCount }) {
  return (
    <div className="rounded-xl border border-cyan-700/40 bg-cyan-950/10 p-3">
      <div className="flex items-start justify-between gap-3">
        <div>
          <div className="text-sm font-bold text-white">Recent AI activity</div>
          <p className="text-xs text-slate-500 mt-1">
            Preference memory signals: {Number(preferenceCount || 0)}
          </p>
        </div>
      </div>

      {!rows?.length ? (
        <p className="text-xs text-slate-500 mt-3">No recent AI activity recorded yet.</p>
      ) : (
        <div className="space-y-2 mt-3">
          {rows.slice(0, 5).map((row) => (
            <div
              key={row.activity_id}
              className="rounded-lg border border-slate-800 bg-slate-950/60 p-2"
            >
              <div className="flex items-start justify-between gap-2">
                <div className="text-xs font-semibold text-slate-200 capitalize">
                  {String(row.activity_label || row.activity_type || "activity").replace(/_/g, " ")}
                </div>
                <div className="text-[11px] text-slate-500 shrink-0">
                  {formatDate(row.created_at)}
                </div>
              </div>

              <div className="text-[11px] text-slate-500 mt-1 capitalize">
                {row.entity_type || "entity"} {row.entity_id ? `#${row.entity_id}` : ""}
              </div>
            </div>
          ))}
        </div>
      )}
    </div>
  );
}

function MiniStat({ label, value, tone = "default" }) {
  const toneClass =
    tone === "high"
      ? "text-orange-200 border-orange-600/40 bg-orange-950/20"
      : tone === "medium"
        ? "text-yellow-200 border-yellow-600/40 bg-yellow-950/20"
        : tone === "low"
          ? "text-cyan-200 border-cyan-600/40 bg-cyan-950/20"
          : tone === "info"
            ? "text-slate-200 border-slate-600/40 bg-slate-900/60"
            : "text-white border-slate-700 bg-slate-900/70";

  return (
    <div className={`rounded-xl border p-3 ${toneClass}`}>
      <div className="text-xs opacity-70">{label}</div>
      <div className="text-xl font-bold">{Number(value || 0)}</div>
    </div>
  );
}

function SmartActionLayer({ insight, busy, onSmartAction }) {
  const [expanded, setExpanded] = useState(false);
  const actions = getSmartActions(insight);
  const visibleActions = expanded ? actions : actions.slice(0, 2);

  return (
    <div className="mt-3 rounded-xl border border-slate-700/60 bg-slate-950/50 p-3">
      <div className="flex items-start justify-between gap-3">
        <div>
          <div className="text-xs font-bold text-cyan-200 uppercase tracking-wide">
            Smart workflow actions
          </div>
          <p className="text-xs text-slate-500 mt-1">
            Turn this insight into an operational response.
          </p>
        </div>

        {actions.length > 2 && (
          <button
            type="button"
            onClick={() => setExpanded((prev) => !prev)}
            className="text-xs text-cyan-300 hover:text-cyan-100"
          >
            {expanded ? "Less" : "More"}
          </button>
        )}
      </div>

      <div className="flex flex-wrap gap-2 mt-3">
        {visibleActions.map((action) => {
          const tone = ACTION_STYLE[action.tone] || ACTION_STYLE.neutral;

          return (
            <button
              key={action.key}
              type="button"
              disabled={busy}
              onClick={() => onSmartAction(action)}
              title={action.description}
              className={`rounded-lg border disabled:opacity-50 disabled:cursor-not-allowed px-3 py-1.5 text-xs font-semibold ${tone}`}
            >
              {action.label}
            </button>
          );
        })}
      </div>
    </div>
  );
}

function InsightCard({
  insight,
  busy,
  onUseful,
  onNotUseful,
  onAcknowledge,
  onResolve,
  onDismiss,
  onSmartAction,
}) {
  const severity = String(insight?.severity || "info").toLowerCase();
  const style = getSeverityStyle(severity);
  const relatedRoute = getRelatedRoute(insight);
  const relatedLabel = getRelatedLabel(insight);
  const entityDisplay = getEntityDisplay(insight);

  const entityLabel =
    ENTITY_LABELS[insight?.entity_type] ||
    String(insight?.entity_type || "Insight").replace(/_/g, " ");

  return (
    <article className={`rounded-xl border p-4 ${style.card}`}>
      <div className="flex flex-col lg:flex-row lg:items-start lg:justify-between gap-3">
        <div className="min-w-0">
          <div className="flex flex-wrap items-center gap-2 mb-2">
            <span className={`h-2.5 w-2.5 rounded-full ${style.dot}`} />

            <span className={`rounded-full border px-2 py-0.5 text-xs ${style.pill}`}>
              {style.label}
            </span>

            {relatedRoute ? (
              <Link
                to={relatedRoute}
                className="rounded-full border border-cyan-500/50 bg-cyan-950/30 px-2 py-0.5 text-xs text-cyan-200 hover:bg-cyan-900/50 hover:text-white capitalize"
              >
                {entityLabel}
                {entityDisplay ? ` · ${entityDisplay}` : ""}
              </Link>
            ) : (
              <span className="rounded-full border border-slate-600 bg-slate-900/70 px-2 py-0.5 text-xs text-slate-300 capitalize">
                {entityLabel}
                {entityDisplay ? ` · ${entityDisplay}` : ""}
              </span>
            )}

            {insight?.confidence != null && (
              <span className="rounded-full border border-slate-700 bg-slate-900/70 px-2 py-0.5 text-xs text-slate-400">
                Confidence {Number(insight.confidence).toFixed(2)}
              </span>
            )}

            {insight?.status && insight.status !== "open" && (
              <span className="rounded-full border border-slate-700 bg-slate-900/70 px-2 py-0.5 text-xs text-slate-400 capitalize">
                {insight.status}
              </span>
            )}
          </div>

          <h3 className="text-base font-bold text-white">{insight?.title || "AI Insight"}</h3>

          <p className="text-sm text-slate-300 mt-2 leading-relaxed">
            {insight?.message || "No message supplied."}
          </p>

          {insight?.recommendation && (
            <div className="mt-3 rounded-lg border border-cyan-700/40 bg-cyan-950/20 p-3">
              <div className="text-xs font-semibold text-cyan-200 uppercase tracking-wide">
                Recommended action
              </div>
              <p className="text-sm text-cyan-50/85 mt-1">{insight.recommendation}</p>
            </div>
          )}

          {relatedRoute && (
            <div className="mt-3">
              <Link
                to={relatedRoute}
                className="inline-flex items-center rounded-lg border border-cyan-500/50 bg-cyan-600/20 hover:bg-cyan-500/30 px-3 py-1.5 text-xs font-semibold text-cyan-100"
              >
                {relatedLabel}
              </Link>
            </div>
          )}

          <SmartActionLayer
            insight={insight}
            busy={busy}
            onSmartAction={onSmartAction}
          />

          <div className="text-xs text-slate-500 mt-3">
            Created: {formatDate(insight?.created_at)}
          </div>
        </div>

        <div className="flex flex-row lg:flex-col gap-2 shrink-0">
          <button
            type="button"
            onClick={onAcknowledge}
            disabled={busy || insight?.status === "acknowledged"}
            className="rounded-lg bg-slate-700 hover:bg-slate-600 disabled:opacity-50 px-3 py-1.5 text-xs font-semibold text-white"
          >
            Ack
          </button>

          <button
            type="button"
            onClick={onResolve}
            disabled={busy}
            className="rounded-lg bg-emerald-700 hover:bg-emerald-600 disabled:opacity-50 px-3 py-1.5 text-xs font-semibold text-white"
          >
            Resolve
          </button>

          <button
            type="button"
            onClick={onDismiss}
            disabled={busy}
            className="rounded-lg bg-red-800 hover:bg-red-700 disabled:opacity-50 px-3 py-1.5 text-xs font-semibold text-white"
          >
            Dismiss
          </button>
        </div>
      </div>

      <div className="flex flex-wrap items-center gap-2 mt-4 border-t border-slate-700/60 pt-3">
        <span className="text-xs text-slate-500">Was this useful?</span>

        <button
          type="button"
          onClick={onUseful}
          disabled={busy}
          className="rounded-md border border-emerald-700/60 bg-emerald-950/30 hover:bg-emerald-900/50 disabled:opacity-50 px-2.5 py-1 text-xs text-emerald-200"
        >
          Useful
        </button>

        <button
          type="button"
          onClick={onNotUseful}
          disabled={busy}
          className="rounded-md border border-slate-700 bg-slate-900 hover:bg-slate-800 disabled:opacity-50 px-2.5 py-1 text-xs text-slate-300"
        >
          Not useful
        </button>

        {busy && <span className="text-xs text-slate-500">Saving…</span>}
      </div>
    </article>
  );
}